/**
 * OneTrust Consent Utility
 *
 * Handles cookie consent via OneTrust with implied consent after 8 seconds
 * per legal team guidance. Listens for explicit user interaction and respects
 * decline choices.
 *
 * Category Groups:
 * - C0001: Strictly Necessary (always active)
 * - C0002: Performance/Analytics
 * - C0003: Functional
 * - C0004: Targeting/Advertising
 */

// ─── Timer Configuration ────────────────────────────────────────────────────
// To disable the implied consent timer, set this to null or remove the
// setTimeout block in onConsent() that references it.
const IMPLIED_CONSENT_DELAY = 8000;
// ─────────────────────────────────────────────────────────────────────────────

const BANNER_HIDE_DELAY = 400;

// ─── Consent Group Helpers ──────────────────────────────────────────────────

const getActiveGroups = () => {
  const groups = window.OnetrustActiveGroups || '';
  return new Set(groups.split(',').filter(Boolean));
};

export const hasConsent = (groupId) => getActiveGroups().has(groupId);
export const hasFunctionalConsent = () => hasConsent('C0003');
export const hasAnalyticsConsent = () => hasConsent('C0002');
export const hasTargetingConsent = () => hasConsent('C0004');

// ─── Banner Fade / Close ────────────────────────────────────────────────────

const fadeBannerOut = () => {
  const attemptFade = (attemptsLeft) => {
    const banner = document.querySelector('#onetrust-banner-sdk');

    if (!banner) {
      if (attemptsLeft > 0) {
        setTimeout(() => attemptFade(attemptsLeft - 1), 200);
      }
      return;
    }

    banner.style.transition = 'opacity 0.4s ease-in-out';
    banner.classList.remove('ot-fade-in');
    banner.classList.add('ot-hide');
    banner.style.opacity = '0';

    setTimeout(() => {
      banner.style.setProperty('display', 'none', 'important');
      banner.style.setProperty('visibility', 'hidden', 'important');
    }, BANNER_HIDE_DELAY);
  };

  attemptFade(5);
};

const closeBanner = () => {
  fadeBannerOut();

  const bannerExists = !!document.querySelector('#onetrust-banner-sdk');
  if (!bannerExists) return;

  try {
    if (window.OneTrust && window.OneTrust.SetAlertBoxClosed) {
      window.OneTrust.SetAlertBoxClosed();
    }

    // Ensure floating cookie preference button remains visible
    // after OneTrust processes the close
    setTimeout(() => {
      const floatingBtn = document.querySelector('#ot-sdk-btn-floating');
      if (floatingBtn) {
        floatingBtn.style.setProperty('display', 'block', 'important');
        floatingBtn.style.setProperty('visibility', 'visible', 'important');
        floatingBtn.style.setProperty('opacity', '1', 'important');
      }
    }, BANNER_HIDE_DELAY);
  } catch (e) {
    console.error('OneTrust.SetAlertBoxClosed failed:', e);
  }
};

// ─── Main Consent Handler ───────────────────────────────────────────────────

/**
 * Initializes consent handling. Call once on page load (e.g. in gatsby-browser).
 *
 * @param {Object} callbacks - Optional callbacks for each consent category.
 * @param {Function} callbacks.onFunctional - Fires when functional consent is granted.
 * @param {Function} callbacks.onAnalytics - Fires when analytics consent is granted.
 * @param {Function} callbacks.onTargeting - Fires when targeting consent is granted.
 * @param {Function} callbacks.onFunctionalRevoked - Fires when functional consent is revoked.
 * @param {Function} callbacks.onAnalyticsRevoked - Fires when analytics consent is revoked.
 * @param {Function} callbacks.onTargetingRevoked - Fires when targeting consent is revoked.
 * @returns {Function} Cleanup function to clear timers.
 */
export const onConsent = (callbacks = {}) => {
  const {
    onFunctional,
    onAnalytics,
    onTargeting,
    onFunctionalRevoked,
    onAnalyticsRevoked,
    onTargetingRevoked,
  } = callbacks;

  let impliedConsentTimeout = null;
  let resolved = false;

  // Track which categories were previously granted so we can detect revocations
  let previouslyGranted = {
    functional: false,
    analytics: false,
    targeting: false,
  };

  const updateGtagConsent = () => {
    if (typeof window.gtag !== 'function') return;
    try {
      window.gtag('consent', 'update', {
        analytics_storage: hasAnalyticsConsent() ? 'granted' : 'denied',
        ad_storage: hasTargetingConsent() ? 'granted' : 'denied',
        ad_user_data: hasTargetingConsent() ? 'granted' : 'denied',
        ad_personalization: hasTargetingConsent() ? 'granted' : 'denied',
        functionality_storage: hasFunctionalConsent() ? 'granted' : 'denied',
        personalization_storage: hasTargetingConsent() ? 'granted' : 'denied',
      });
    } catch (e) {
      console.error('GTM consent update failed:', e);
    }
  };

  const handleConsentChange = () => {
    updateGtagConsent();

    const currentFunctional = hasFunctionalConsent();
    const currentAnalytics = hasAnalyticsConsent();
    const currentTargeting = hasTargetingConsent();

    if (!resolved) {
      // First resolution
      resolved = true;
      if (impliedConsentTimeout) clearTimeout(impliedConsentTimeout);

      const groups = Array.from(getActiveGroups());
      sessionStorage.setItem('consentGroups', JSON.stringify(groups));

      if (onFunctional && currentFunctional) onFunctional();
      if (onAnalytics && currentAnalytics) onAnalytics();
      if (onTargeting && currentTargeting) onTargeting();
    } else {
      // Already resolved — check for revocations and new grants
      if (previouslyGranted.functional && !currentFunctional) {
        if (onFunctionalRevoked) onFunctionalRevoked();
      }
      if (previouslyGranted.analytics && !currentAnalytics) {
        if (onAnalyticsRevoked) onAnalyticsRevoked();
      }
      if (previouslyGranted.targeting && !currentTargeting) {
        if (onTargetingRevoked) onTargetingRevoked();
      }

      // Handle late grants (user re-enabled a category)
      if (!previouslyGranted.functional && currentFunctional) {
        if (onFunctional) onFunctional();
      }
      if (!previouslyGranted.analytics && currentAnalytics) {
        if (onAnalytics) onAnalytics();
      }
      if (!previouslyGranted.targeting && currentTargeting) {
        if (onTargeting) onTargeting();
      }

      const groups = Array.from(getActiveGroups());
      sessionStorage.setItem('consentGroups', JSON.stringify(groups));
    }

    // Update tracking of what's currently granted
    previouslyGranted = {
      functional: currentFunctional,
      analytics: currentAnalytics,
      targeting: currentTargeting,
    };
  };

  const fireCallbacks = () => {
    handleConsentChange();
  };

  // If consent was already resolved in this session, restore and fire
  const storedGroups = sessionStorage.getItem('consentGroups');
  if (storedGroups) {
    let groups = null;
    try {
      groups = JSON.parse(storedGroups);
    } catch {
      sessionStorage.removeItem('consentGroups');
    }

    if (groups) {
      if (
        !window.OnetrustActiveGroups ||
        window.OnetrustActiveGroups.replace(/,/g, '') === ''
      ) {
        window.OnetrustActiveGroups = ',' + groups.join(',') + ',';
      }
      updateGtagConsent();
      fireCallbacks();
      const bannerTimeout = setTimeout(() => closeBanner(), IMPLIED_CONSENT_DELAY);
      return () => clearTimeout(bannerTimeout);
    }
  }

  // Listen for explicit user interaction with the OneTrust banner.
  // We use both the JS API (if available) and a custom event dispatched
  // from OptanonWrapper in html.js to cover all timing scenarios.
  if (window.OneTrust && window.OneTrust.OnConsentChanged) {
    window.OneTrust.OnConsentChanged(() => {
      handleConsentChange();
      closeBanner();
    });
  }

  window.addEventListener('oneTrustConsentChanged', () => {
    handleConsentChange();
    closeBanner();
  });

  // ─── Implied Consent Timer (8 seconds) ──────────────────────────────────
  // If your PM decides to remove the auto-close behavior, delete or comment
  // out this entire block. The banner will then only close on explicit
  // user interaction (accept/reject buttons).
  if (IMPLIED_CONSENT_DELAY) {
    impliedConsentTimeout = setTimeout(() => {
      if (resolved) return;

      const bannerShown = !!document.querySelector('#onetrust-consent-sdk');
      if (!bannerShown) return;

      // If OneTrust hasn't been interacted with, groups may be empty.
      // Per legal guidance, treat 8s of browsing as implied consent.
      if (
        !window.OnetrustActiveGroups ||
        window.OnetrustActiveGroups.replace(/,/g, '') === ''
      ) {
        window.OnetrustActiveGroups = ',C0001,C0002,C0003,C0004,';
      }

      updateGtagConsent();
      fireCallbacks();
      closeBanner();
    }, IMPLIED_CONSENT_DELAY);
  }
  // ─── End Implied Consent Timer ──────────────────────────────────────────

  return () => {
    if (impliedConsentTimeout) clearTimeout(impliedConsentTimeout);
  };
};

// ─── OptanonWrapper Hook ────────────────────────────────────────────────────
// OneTrust calls window.OptanonWrapper on consent changes. We expose a handler
// that can be assigned from html.js or gatsby-browser.js.
let _onConsentChangeHandler = null;

export const setConsentChangeHandler = (handler) => {
  _onConsentChangeHandler = handler;
};

/**
 * Call this from the global OptanonWrapper function.
 * It bridges OneTrust's callback into our consent system.
 */
export const handleOptanonWrapper = () => {
  if (_onConsentChangeHandler) {
    _onConsentChangeHandler();
  }
};
